import React, { useState } from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

import { Image, Row, S, Tooltip } from './styles';
import { FlagIcon } from '../../../../../components/Icons';
import { useFlag } from '@unleash/proxy-client-react';

import Cautelar from '../../../../../images/icons/badge_cautelar_aprovada.svg';
import CautelarInstaCarro from '../../../../../images/icons/badge_instacarro_cautelar_aprovada.svg';
import Km from '../../../../../images/icons/km.svg';
import Longarina from '../../../../../images/icons/badge_longarina.svg';
import Colunas from '../../../../../images/icons/badge_colunas.svg';
import Cambio from '../../../../../images/icons/badge_cambio.svg';
import Scanner from '../../../../../images/icons/badge_scanner.svg';
import { checkItemsToHaveDivider } from '../../../../../utils/details';
import BadgePneus from '../../../../../images/icons/badge_pneus.svg'; // Novo ícone para pneus
import BadgeInterior from '../../../../../images/icons/badge_interior.svg'; // Novo ícone para conservação do interior
import BadgeGNV from '../../../../../images/icons/badge_gnv.svg'; // Novo ícone para GNV
import BadgeMotor from '../../../../../images/icons/badge_motor.svg'; // Badge de motor sem apontamentos
import CheckIcon from '../../../../../images/icons/check_detail.svg'; // Ícone de check
import AttentionIcon from '../../../../../images/icons/attention_detail.svg'; // Ícone de atenção
import IndeterminateIcon from '../../../../../images/icons/indeterminate.svg'; // Ícone de indeterminado

function sortbyImages(items) {
  const photos = [];
  const texts = [];
  items.map(el => (el.img && el.img.src ? photos.push(el) : texts.push(el)));
  return { photos: photos, texts: texts };
}

function sortbyImages2(items) {
  const photos = [];
  const texts = [];
  items.map(el => (el.label && el.label.includes('Foto') ? photos.push(el) : texts.push(el)));
  return { photos: photos, texts: texts };
}

// Função helper para determinar qual ícone mostrar baseado no valor
function getIconForValue(value, needsAttention, label = null) {
  // Extrair o valor como string (pode vir como objeto ou string)
  let valueString = '';
  if (typeof value === 'string') {
    valueString = value;
  } else if (value && typeof value === 'object' && value.value) {
    valueString = value.value;
  } else if (value && typeof value === 'object' && value.description) {
    valueString = value.description;
  }

  // Função auxiliar para normalizar e verificar se contém "não equipado"
  const normalizeAndCheck = str => {
    if (!str || typeof str !== 'string') return false;
    return str
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^\w\s]/g, ' ') // Remove pontuação e caracteres especiais, substituindo por espaço
      .replace(/\s+/g, ' ') // Normaliza espaços múltiplos
      .trim()
      .includes('nao equipado');
  };

  // Verificar se o valor ou o label contém "não equipado"
  if (normalizeAndCheck(valueString) || (label && normalizeAndCheck(label))) {
    return { icon: IndeterminateIcon, alt: 'Indeterminado' };
  }

  // Se needsAttention é null ou undefined, não mostrar ícone
  if (needsAttention === null || needsAttention === undefined) {
    return null;
  }

  // Se needsAttention é true, mostrar ícone de atenção
  if (needsAttention) {
    return { icon: AttentionIcon, alt: 'Atenção' };
  }

  // Se needsAttention é false, mostrar ícone de check
  return { icon: CheckIcon, alt: 'Check' };
}

export const Content = ({
  title,
  items,
  auction,
  onGallery,
  isOptional = false,
  collapsed = false,
  useNewSort = false,
  useNewLayout = false,
}) => {
  const sortedItems = useNewSort ? sortbyImages2(items) : sortbyImages(items);
  const [opened, setOpened] = useState(collapsed);

  const flagBadgeCautelar = useFlag('badge-cautelar');
  const flagBadgeLongarina = useFlag('badge-longarina');
  const flagBadgeColunas = useFlag('badge-colunas');
  const flagBadgeKm = useFlag('badge-km');
  const flagBadgeCambio = useFlag('badge-cambio');
  const flagBadgeScanner = useFlag('badge-scanner');
  const flagBadgePneus = useFlag('badge-pneus'); // Nova flag para badge de pneus
  const flagBadgeInterior = useFlag('badge-interior'); // Nova flag para badge de conservação do interior
  const flagBadgeGNV = useFlag('badge-gnv'); // Nova flag para badge de GNV

  const shouldShowCautelar = auction?.vehicle?.precautionaryReport && flagBadgeCautelar;
  const shouldShowLongarina =
    auction?.vehicle?.canHaveCarSparBadge && flagBadgeLongarina && !shouldShowCautelar;
  const shouldShowColunas =
    auction?.vehicle?.canHaveColumnBadge && flagBadgeColunas && !shouldShowCautelar;
  const shouldShowBomKm = auction?.vehicle?.kilometrageAcceptable && flagBadgeKm;
  const shouldShowCambio = auction?.vehicle?.canHaveTransmissionBadge && flagBadgeCambio;
  const shouldShowScanner = auction?.vehicle?.canHaveScannerBadge && flagBadgeScanner;
  // Nova lógica para badge de pneus
  const shouldShowPneus = auction?.vehicle?.canHaveTiresBadge && flagBadgePneus;

  // Nova lógica para badge de conservação do interior
  const shouldShowInterior = auction?.vehicle?.canHaveInteriorBadge && flagBadgeInterior;

  // Nova lógica para badge de GNV
  const shouldShowGNV = auction?.vehicle?.canHaveGNVBadge && flagBadgeGNV;

  // Lógica para badge de motor sem apontamentos
  const shouldShowMotor = auction?.vehicle?.canHaveEngineBadge;

  const BADGE_TYPES = {
    // Ordem conforme documento: ESTRUTURA -> MECÂNICA -> VISUAL -> EQUIPAMENTOS -> PESQUISA -> MARGEM
    Estrutura: ['BadgeCautelar', 'BadgeLongarina', 'BadgeColunas', 'BadgeBomKm'],
    Mecânica: ['BadgeMotor', 'BadgeCambio', 'BadgeScanner'], // Ordem: Motor, Câmbio, Scanner
    Visual: ['BadgeInterior'], // Bom estado de conservação interior
    // GNV e Pneus estão em EQUIPAMENTOS no documento, mas aqui são tratados separadamente
  };

  const BADGE_STATUS = {
    BadgeCautelar: shouldShowCautelar,
    BadgeLongarina: shouldShowLongarina,
    BadgeColunas: shouldShowColunas,
    BadgeBomKm: shouldShowBomKm,
    BadgeCambio: shouldShowCambio,
    BadgeScanner: shouldShowScanner,
    BadgeInterior: shouldShowInterior, // Nova badge de conservação do interior
    BadgeGNV: shouldShowGNV, // Nova badge de GNV
    BadgeMotor: shouldShowMotor, // Badge de motor sem apontamentos
    // Removendo BadgePneus daqui - será tratada separadamente
  };

  const BADGE_DESCRIPTIONS_TYPES = {
    Estrutura: {
      badges: [
        'BadgeCautelar',
        'BadgeCautelarBomKm',
        'BadgeLongarina',
        'BadgeLongarinaBomKm',
        'BadgeColunas',
        'BadgeColunasBomKm',
        'BadgeBomKm',
        'BadgeLongarinaColunas',
        'AllStructureBadges',
      ],
      color: '#FFE8D8',
      bgColor: '#FF6C1C',
    },
    Mecânica: {
      badges: ['BadgeMotor', 'BadgeCambio', 'BadgeScanner', 'AllMechanicBadges'], // Ordem: Motor, Câmbio, Scanner
      color: '#BCF738',
      bgColor: '#007E62',
    },
    Visual: {
      badges: ['BadgeInterior'],
      color: '#171C24',
      bgColor: '#BCF738',
    },
    Pneus: {
      badges: ['BadgePneus'],
      color: '#E8F5E8',
      bgColor: '#ffff',
    },
  };

  const BADGE_DESCRIPTIONS_STATUS = {
    BadgeCautelar: shouldShowCautelar && !shouldShowBomKm,
    BadgeCautelarBomKm: shouldShowCautelar && shouldShowBomKm,
    BadgeLongarina: shouldShowLongarina && !shouldShowBomKm && !shouldShowColunas,
    BadgeLongarinaBomKm: shouldShowLongarina && shouldShowBomKm && !shouldShowColunas,
    BadgeColunas: shouldShowColunas && !shouldShowBomKm && !shouldShowLongarina,
    BadgeBomKm:
      shouldShowBomKm && !shouldShowCautelar && !shouldShowColunas && !shouldShowLongarina,
    BadgeColunasBomKm: shouldShowColunas && shouldShowBomKm && !shouldShowLongarina,
    BadgeLongarinaColunas: shouldShowLongarina && shouldShowColunas && !shouldShowBomKm,
    AllStructureBadges: shouldShowLongarina && shouldShowColunas && shouldShowBomKm,
    BadgeCambio: shouldShowCambio && !shouldShowScanner && !shouldShowMotor,
    BadgeScanner: shouldShowScanner && !shouldShowCambio && !shouldShowMotor,
    BadgeMotor: shouldShowMotor && !shouldShowCambio && !shouldShowScanner,
    AllMechanicBadges: shouldShowCambio && shouldShowScanner && shouldShowMotor,
    BadgePneus: shouldShowPneus, // Nova badge de pneus
    BadgeInterior: shouldShowInterior, // Nova badge de conservação do interior
    BadgeGNV: shouldShowGNV, // Nova badge de GNV
  };

  const BADGE_DESCRIPTIONS = {
    BadgeCautelar: auction?.vehicle?.hasD2D
      ? 'InstaCarro Cautelar aprovada.'
      : 'Cautelar aprovada.',
    BadgeCautelarBomKm: auction?.vehicle?.hasD2D
      ? 'InstaCarro Cautelar aprovada, boa km por ano.'
      : 'Cautelar aprovada, boa km por ano.',
    BadgeLongarina: 'Longarina sem apontamentos.',
    BadgeLongarinaBomKm: 'Longarina sem apontamentos, boa km por ano.',
    BadgeBomKm: 'Boa km por ano.',
    BadgeColunas: 'Colunas sem apontamentos.',
    BadgeColunasBomKm: 'Colunas sem apontamentos, boa km por ano.',
    BadgeLongarinaColunas: 'Longarina e colunas sem apontamentos.',
    AllStructureBadges: 'Longarina e colunas sem apontamentos, boa km por ano.',
    BadgeCambio: 'Câmbio sem apontamentos.',
    BadgeScanner: 'Scanner sem apontamentos.',
    BadgeMotor: 'Motor sem apontamentos.',

    BadgePneus: 'Pneus em condição de rodagem.', // Nova descrição
    BadgeInterior: 'Bom estado de conservação interior.', // Nova descrição
    BadgeGNV: 'Equipado com GNV.', // Nova descrição
  };

  const BADGES = [
    {
      icone: auction?.vehicle?.hasD2D ? CautelarInstaCarro : Cautelar,
      titulo: auction?.vehicle?.hasD2D ? 'Cautelar aprovada' : 'Cautelar aprovada',
      descricao: auction?.vehicle?.hasD2D
        ? 'Aprovada, pode conter apontamentos'
        : 'Cautelar Visão total verificada e aprovada',
    },
    {
      icone: Longarina,
      titulo: 'Longarina sem apontamentos',
      descricao: 'Verificada e sem observações',
    },
    {
      icone: Colunas,
      titulo: 'Colunas sem apontamentos',
      descricao: 'Verificadas e sem observações',
    },
    {
      icone: Km,
      titulo: 'Boa km por ano',
      descricao: 'Na média de km para carros do mesmo ano',
    },
    {
      icone: Cambio,
      titulo: 'Câmbio sem apontamentos',
      descricao: 'Teste inerte sem ressalvas',
    },
    {
      icone: Scanner,
      titulo: 'Scanner sem apontamentos',
      descricao: 'Verificado e sem observações',
    },
    {
      icone: BadgePneus, // Nova badge de pneus
      titulo: 'Pneus em condição de rodagem',
    },
    {
      icone: BadgeInterior, // Nova badge de conservação do interior
      titulo: 'Bom estado de conservação interior',
    },
    {
      icone: BadgeGNV, // Nova badge de GNV
      titulo: 'Equipado com GNV',
    },
    {
      icone: BadgeMotor, // Badge de motor sem apontamentos
      titulo: 'Motor sem apontamentos',
      descricao: 'Verificado e sem observações',
    },
  ];

  const badgeKeyForStatus = Object.keys(BADGE_STATUS).filter(key => BADGE_STATUS[key]);

  const getTrueBadgesByType = type => {
    const badgeKeyForType = BADGE_TYPES[type] || [];
    const trueBadgesKeys = badgeKeyForStatus.filter(key => badgeKeyForType.includes(key));
    return trueBadgesKeys.reduce((result, key) => {
      // Mapeamento correto dos badges para seus índices no array BADGES
      const badgeIndexMap = {
        BadgeCautelar: 0,
        BadgeLongarina: 1,
        BadgeColunas: 2,
        BadgeBomKm: 3,
        BadgeCambio: 4,
        BadgeScanner: 5,
        BadgePneus: 6,
        BadgeInterior: 7,
        BadgeGNV: 8,
        BadgeMotor: 9,
      };

      const badgeIndex = badgeIndexMap[key];
      if (badgeIndex !== undefined && BADGES[badgeIndex]) {
        result[key] = BADGES[badgeIndex];
      }
      return result;
    }, {});
  };

  const descriptionKeyForStatus = Object.keys(BADGE_DESCRIPTIONS_STATUS).filter(
    key => BADGE_DESCRIPTIONS_STATUS[key]
  );

  const getTrueDescription = type => {
    const descriptionKeyForType = BADGE_DESCRIPTIONS_TYPES[type];

    if (!descriptionKeyForType) {
      console.warn(`Tipo inválido: ${type}`);
      return [];
    }

    const { badges, color, bgColor } = descriptionKeyForType;

    const trueDescriptionKeys = descriptionKeyForStatus.filter(key => badges.includes(key));

    const descriptions = trueDescriptionKeys.map(key => ({
      description: BADGE_DESCRIPTIONS[key],
      color,
      bgColor,
    }));

    // Tratamento especial para badge GNV na seção Mecânica
    if (type === 'Mecânica' && shouldShowGNV) {
      descriptions.push({
        description: BADGE_DESCRIPTIONS['BadgeGNV'],
        color: '#F3F3EF', // Cor da badge de Pneus
        bgColor: '#8B8D91', // Cor da badge de Pneus
      });
    }

    return descriptions;
  };

  const badgesToDisplay = getTrueBadgesByType(title);
  const descriptionToDisplay = getTrueDescription(title);

  if (!items.length) return null;

  // Verificar se esta é a seção de "Rodas e Pneus"
  const isRodasEPneusSection =
    title === 'Rodas e Pneus' || title.includes('Pneus') || title.includes('Rodas');

  const useTitleLayout = () => {
    if (!title) return null;

    return (
      <S.Title onClick={() => setOpened(!opened)}>
        <span className="dropdown-list-title">
          {title.includes('Mecanica') ? 'Mecânica' : title}
        </span>

        <Row>
          {/* Exibir badges normais da seção */}
          {Object.entries(badgesToDisplay).map(([key, badge]) => (
            <Image key={key} src={badge.icone} alt={`Badge ${key}`} />
          ))}

          {/* Exibir badge de pneus na seção "Rodas e Pneus" - posicionada como "Cautelar aprovada" */}
          {isRodasEPneusSection && shouldShowPneus && <Image src={BadgePneus} alt="Badge Pneus" />}
        </Row>

        {opened ? <MdKeyboardArrowDown size={27} /> : <MdKeyboardArrowUp size={27} />}
      </S.Title>
    );
  };

  const sortedItemsToDisplayWithoutGroup = sortedItems?.texts
    ?.filter(item => item.value && !item.label.includes('>:'))
    .sort((a, b) => a.value.length - b.value.length);

  const sortedItemsToDisplayWithGroup = () => {
    const grouped = Object.values(
      sortedItems?.texts
        ?.filter(item => item.value && item.label.includes('>:'))
        .reduce((acc, item) => {
          const [group, subLabel] = item.label.split('>:').map(s => s.trim());

          if (!acc[group]) {
            acc[group] = { group, items: [] };
          }

          acc[group].items.push({
            label: subLabel,
            value: item.value,
            needsAttention: item.needsAttention,
          });

          acc[group].items.sort((a, b) => a.value.length - b.value.length);

          return acc;
        }, {})
    );

    return grouped;
  };

  return (
    <S.Wrapper useNewSort={useNewSort || useNewLayout}>
      {useTitleLayout()}

      {opened && !!sortedItems?.photos?.length ? (
        <React.Fragment>
          {/* Exibir descrições das badges normais - incluindo a de pneus automaticamente */}
          {descriptionToDisplay.map(({ description, color, bgColor }) => (
            <Tooltip key={description} color={color} bgColor={bgColor}>
              {description}
            </Tooltip>
          ))}

          {/* Removendo a lógica separada - a badge de pneus será exibida automaticamente */}

          {/* Exibir descrição da badge de pneus na seção "Rodas e Pneus" */}
          {isRodasEPneusSection && shouldShowPneus && (
            <Tooltip key="pneus" color="#F3F3EF" bgColor="#8B8D91">
              Pneus em condição de rodagem
            </Tooltip>
          )}

          {onGallery && (
            <u className="see-photos" onClick={onGallery}>
              Ver Fotos
            </u>
          )}
          {!useNewSort && (
            <S.WithoutImage isOptional={isOptional} useGrid>
              {sortedItems?.photos?.map(({ description, needsAttention }) => {
                const iconData = getIconForValue(description, needsAttention);
                return (
                  <span className="onlyText 44" key={description}>
                    {iconData && (
                      <span style={{ marginRight: '8px', display: 'inline-block' }}>
                        <img
                          src={iconData.icon}
                          alt={iconData.alt}
                          style={{
                            width: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                            height: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                            verticalAlign: 'text-top',
                          }}
                        />
                      </span>
                    )}
                    {description}
                  </span>
                );
              })}
            </S.WithoutImage>
          )}
        </React.Fragment>
      ) : null}
      {opened && !!sortedItems?.texts?.length && !useNewSort ? (
        <S.WithoutImage isOptional={isOptional} useGrid>
          {checkItemsToHaveDivider(sortedItems?.texts || [], true).map(({ value, useDivider }) => {
            const needsAttention = value?.needsAttention;
            const description = value?.description || value;
            const iconData = getIconForValue(description, needsAttention);
            return (
              <span className={useDivider ? 'onlyText' : 'last-item'} key={description}>
                {iconData && (
                  <span style={{ marginRight: '8px', display: 'inline-block' }}>
                    <img
                      src={iconData.icon}
                      alt={iconData.alt}
                      style={{
                        width: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                        height: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                        verticalAlign: 'text-top',
                      }}
                    />
                  </span>
                )}
                {description}
              </span>
            );
          })}
        </S.WithoutImage>
      ) : null}

      {opened && !!sortedItems?.texts?.length && useNewSort ? (
        <>
          {(() => {
            const itemsWithDivider = checkItemsToHaveDivider(
              sortedItemsToDisplayWithoutGroup || [],
              true
            );
            const attentionItems = itemsWithDivider.filter(
              ({ value }) => value?.needsAttention === true
            );
            const okItems = itemsWithDivider.filter(({ value }) => value?.needsAttention !== true);

            const renderItem = ({ value: { label, value, needsAttention }, useDivider }) => {
              const forceDivider = label && label.toLowerCase().includes('laudo cautelar');
              const className = forceDivider || useDivider ? 'onlyText' : 'last-item';
              const iconData = getIconForValue(value, needsAttention, label);
              return (
                <div key={label} className={className}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {iconData && (
                      <span style={{ marginRight: '8px', display: 'inline-block', flexShrink: 0 }}>
                        <img
                          src={iconData.icon}
                          alt={iconData.alt}
                          style={{
                            width: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                            height: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                            verticalAlign: 'text-top',
                          }}
                        />
                      </span>
                    )}
                    <div style={{ flex: 1 }}>
                      <span className="onlyBoldText">{label}</span>
                      <span>{': '}</span>
                      {value.includes(',') ? (
                        <>
                          <br />
                          {value.split(',').map(item => (
                            <>
                              <span key={item} className="groupedValue">
                                {item.replaceAll(';', ',')}
                              </span>
                              <br />
                            </>
                          ))}
                        </>
                      ) : (
                        <span>{value.replaceAll(';', ',')}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <>
                {/* Bloco 1: itens com atenção */}
                {attentionItems.length > 0 && (
                  <S.WithoutImage isOptional={isOptional} useGrid>
                    {attentionItems.map(renderItem)}
                  </S.WithoutImage>
                )}

                {/* Bloco 2: itens OK (checks) abaixo */}
                {okItems.length > 0 && (
                  <S.WithoutImage isOptional={isOptional} useGrid>
                    {okItems.map(renderItem)}
                  </S.WithoutImage>
                )}
              </>
            );
          })()}
        </>
      ) : null}
      {opened && !!sortedItems?.texts?.length && useNewSort ? (
        <S.WithoutImage isOptional={isOptional}>
          {sortedItemsToDisplayWithGroup()?.map(({ group, items }, index) => {
            const attentionItems = (items || []).filter(i => i?.needsAttention === true);
            const okItems = (items || []).filter(i => i?.needsAttention !== true);

            const renderItem = ({ label, value, needsAttention }, useDivider) => {
              const forceDivider = label && label.toLowerCase().includes('laudo cautelar');
              // Do not use divider for "Sem danos" inside the group "Longarina e colunas"
              const isSemDanosInLongarinaColunas =
                group === 'Longarina e colunas' &&
                label &&
                label.trim().toLowerCase() === 'sem danos';
              const shouldUseDivider =
                (forceDivider || useDivider) && !isSemDanosInLongarinaColunas;
              const className = shouldUseDivider ? 'onlyText' : 'last-item';
              const iconData = getIconForValue(value, needsAttention, label);
              return (
                <div key={label} className={className}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {iconData && (
                      <span style={{ marginRight: '8px', display: 'inline-block', flexShrink: 0 }}>
                        <img
                          src={iconData.icon}
                          alt={iconData.alt}
                          style={{
                            width: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                            height: iconData.icon === IndeterminateIcon ? '16px' : '20px',
                            verticalAlign: 'text-top',
                          }}
                        />
                      </span>
                    )}
                    <div style={{ flex: 1 }}>
                      <span className="onlyBoldText">{label}</span>
                      <span>{': '}</span>
                      {value.includes(',') ? (
                        <>
                          <br />
                          {value.split(',').map(item => (
                            <>
                              <span key={item} className="groupedValue">
                                {item.replaceAll(';', ',')}
                              </span>
                              <br />
                            </>
                          ))}
                        </>
                      ) : (
                        <span>{value.replaceAll(';', ',')}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <div
                key={group}
                className={
                  index === sortedItemsToDisplayWithGroup().length - 1 ? 'last-item' : 'onlyText'
                }
              >
                <span
                  className="onlyBoldHeader"
                  style={
                    group === 'Longarina e colunas'
                      ? { display: 'inline-block', marginTop: '16px' }
                      : undefined
                  }
                >
                  {group}
                </span>
                <br />
                <br />
                {/* Bloco 1: atenção dentro do grupo */}
                {attentionItems.length > 0 && (
                  <S.Group>
                    {checkItemsToHaveDivider(attentionItems, true).map(({ value, useDivider }) =>
                      renderItem(value, useDivider)
                    )}
                  </S.Group>
                )}
                {/* Bloco 2: OK dentro do grupo, abaixo */}
                {okItems.length > 0 && (
                  <S.Group>
                    {checkItemsToHaveDivider(okItems, true).map(({ value, useDivider }) =>
                      renderItem(value, useDivider)
                    )}
                  </S.Group>
                )}
              </div>
            );
          })}
        </S.WithoutImage>
      ) : null}
    </S.Wrapper>
  );
};

export const Container = ({
  title,
  hideIcon = false,
  color,
  children,
  hideLine = false,
  icon = <FlagIcon color={color} />,
  useNewLayout = false,
}) => {
  return (
    <S.Container>
      <S.Title useNewLayout={useNewLayout}>
        {!hideIcon ? icon : null}
        {title && <span className="group-title">{title}</span>}
      </S.Title>
      {children}
      {!hideLine ? <S.Divider /> : null}
    </S.Container>
  );
};

export const NewSection = {
  Container,
  Content,
};
